import { createContext, ReactNode, useContext, useEffect, useMemo } from 'react'
import useFetchGiftReceivedDetails from '@/src/common/hooks/useFetchGiftReceivedDetails'
import { IGift } from '@/src/common/queries/FetchReceivedGiftDetails'
import { usePGPlusStepAndModalContext } from '@/src/common/contexts/PGPlusModalAndStepContext'
import { scrollToTop } from '@/src/common/utilities/scrollToTop'
import {
    DISPATCH_CARD_LINK_MODAL,
    DISPATCH_SET_GIFT_STATE,
} from '@/src/common/reducers/constants'
import { GIFT_DETAILS_STEP } from '@/src/modules/pg-plus/redemption/constants'
import { PG_PLUS_SLUG } from '@/prisma/schemaConstants'
import { CARD_LINK } from '@/src/modules/pg-plus/constants'

// this context handles the account received gifts flow
type TReceivedGifts = {
    giftData: IGift | undefined
    giftIsLoading: boolean
    giftIsSuccess: boolean
    refetchGift: Function
}

export const ReceivedGiftsContext = createContext<TReceivedGifts | null>(null)

export const useReceivedGiftsContext = (): TReceivedGifts => {
    const context = useContext(ReceivedGiftsContext)

    if (!context) {
        throw new Error('Please use ReceivedGiftsContext in parent component')
    }

    return context
}

export const ReceivedGiftsProvider = ({
    children,
    giftUuid,
}: {
    children: ReactNode
    giftUuid: string
}) => {
    const {
        data,
        isLoading: giftIsLoading,
        isSuccess: giftIsSuccess,
        refetch: refetchGift,
    } = useFetchGiftReceivedDetails(giftUuid)
    const giftData = data?.data

    const { giftState, dispatch, isUnbrandedToLink, setSelection } =
        usePGPlusStepAndModalContext()
    const { brandSlug } = giftState

    // set the gift state when data is available
    useEffect(() => {
        if (giftData) {
            dispatch({
                type: DISPATCH_SET_GIFT_STATE,
                payload: {
                    cardUuid: giftData?.card_uuid,
                    brand: giftData?.brand,
                    brandSlug: giftData?.brand_slug,
                    denomination: giftData?.denomination,
                    stepName: GIFT_DETAILS_STEP,
                    isPgPlus: giftData?.is_pg_plus ?? false,
                    createdAt: giftData?.created_at ?? undefined,
                    is24HoursOld: giftData?.is_24_hours_old ?? false,
                },
            })
        }
    }, [giftData, dispatch, refetchGift])

    useEffect(() => {
        scrollToTop('__next')
    }, [giftState?.stepName])

    // if user wants to redeem pg+ for card link, dispatch that modal
    useEffect(() => {
        if (isUnbrandedToLink && brandSlug !== PG_PLUS_SLUG) {
            setSelection(CARD_LINK)
            dispatch({ type: DISPATCH_CARD_LINK_MODAL })
        }
    }, [brandSlug, dispatch, isUnbrandedToLink, setSelection])

    const values = useMemo(
        () => ({
            giftData,
            giftIsLoading,
            giftIsSuccess,
            refetchGift,
        }),
        [giftData, giftIsLoading, giftIsSuccess, refetchGift]
    )

    return (
        <ReceivedGiftsContext.Provider value={values}>
            {children}
        </ReceivedGiftsContext.Provider>
    )
}
